import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/api';
import { Icons } from '../components/icons';

type QuotaByPlan = Record<string, number>;

type AIForm = {
  enabled: boolean;
  endpoint: string;
  default_model: string;
  api_key: string;
  api_key_set?: boolean;
  api_key_masked?: string;
  quota_by_plan: QuotaByPlan;
};

const defaultQuotas = (): QuotaByPlan => ({
  trial: 50000,
  free: 50000,
  starter: 500000,
  pro: 2000000,
  enterprise: 999999999,
});

const emptyForm = (): AIForm => ({
  enabled: false,
  endpoint: 'https://api.openai.com/v1/chat/completions',
  default_model: 'gpt-4o-mini',
  api_key: '',
  quota_by_plan: defaultQuotas(),
});

export function AISettings() {
  const [form, setForm] = useState<AIForm>(emptyForm());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setMsg('');
    apiFetch('/api/v1/platform/ai-settings').then(d => {
      if (d.code === 0 && d.data) {
        const data = d.data as Record<string, any>;
        setForm({
          enabled: !!data.enabled,
          endpoint: data.endpoint || 'https://api.openai.com/v1/chat/completions',
          default_model: data.default_model || 'gpt-4o-mini',
          api_key: '',
          api_key_set: data.api_key_set,
          api_key_masked: data.api_key_masked,
          quota_by_plan: { ...defaultQuotas(), ...(data.quota_by_plan || {}) },
        });
      } else setMsg(d.message || '加载失败');
      setLoading(false);
    }).catch(() => { setMsg('网络错误'); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setMsg('');
    const d = await apiFetch('/api/v1/platform/ai-settings', {
      method: 'PUT',
      body: {
        enabled: form.enabled,
        endpoint: form.endpoint.trim(),
        default_model: form.default_model.trim(),
        api_key: form.api_key.trim() || undefined,
        quota_by_plan: form.quota_by_plan,
      },
    });
    setSaving(false);
    if (d.code === 0) {
      setMsg('已保存');
      load();
    } else setMsg(d.message || '保存失败');
  };

  const setQuota = (plan: string, val: string) => {
    const n = parseInt(val, 10);
    setForm(f => ({
      ...f,
      quota_by_plan: { ...f.quota_by_plan, [plan]: Number.isFinite(n) ? n : 0 },
    }));
  };

  if (loading) return <div style={{ padding: 24 }}>加载中…</div>;

  return (
    <div style={{ maxWidth: 720 }}>
      <h2 style={{ fontSize: 18, fontWeight: 650, marginBottom: 8 }}>AI 服务配置</h2>
      <p style={{ fontSize: 13, color: 'var(--brand-text-muted)', marginBottom: 20, lineHeight: 1.6 }}>
        平台统一托管的 AI 上游（OpenAI 兼容）。租户默认使用平台 AI 并按套餐 Token 配额计费；仅当平台授权时租户才可配置 BYOK。
      </p>
      {msg && <div style={{ marginBottom: 12, fontSize: 13, color: msg === '已保存' ? '#16a34a' : '#dc2626' }}>{msg}</div>}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title"><Icons.Zap /> 平台 AI 上游</div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, marginBottom: 16, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.enabled} onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))} />
          启用平台 AI
        </label>
        <div className="form-group">
          <label>API Endpoint</label>
          <input className="form-input" value={form.endpoint} onChange={e => setForm(f => ({ ...f, endpoint: e.target.value }))} placeholder="https://api.openai.com/v1/chat/completions" />
        </div>
        <div className="form-group">
          <label>默认模型</label>
          <input className="form-input" value={form.default_model} onChange={e => setForm(f => ({ ...f, default_model: e.target.value }))} placeholder="gpt-4o-mini" />
        </div>
        <div className="form-group">
          <label>API Key</label>
          <input className="form-input" type="password" value={form.api_key} onChange={e => setForm(f => ({ ...f, api_key: e.target.value }))}
            placeholder={form.api_key_set ? `已配置 ${form.api_key_masked || '****'}，留空不修改` : 'sk-...'} />
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">套餐 Token 配额（每月）</div>
        <p style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginBottom: 12 }}>可在租户详情中单独覆盖配额；0 表示使用此处套餐默认值。</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {Object.entries(form.quota_by_plan).map(([plan, quota]) => (
            <div key={plan} className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ textTransform: 'capitalize' }}>{plan}</label>
              <input className="form-input" type="number" value={quota} onChange={e => setQuota(plan, e.target.value)} />
            </div>
          ))}
        </div>
      </div>

      <button className="btn btn-primary" disabled={saving} onClick={save}>{saving ? '保存中…' : '保存配置'}</button>
    </div>
  );
}
